'use client';

import { faWarning } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from 'cn';

import { ProjectResponseAudio } from '@/shared-survey';
import { OptionRenderType } from '@/survey-graphql';

import { ProjectResponseAssetItem } from './project-response-asset-item';
import ProjectResponseMessageRendererAudio from './project-response-message-renderer-audio';
import { ProjectResponseQuestionText } from './project-response-question-text';
import {
  getHighlightedRatingValue,
  isRatingValueSelected,
  renderValue,
} from './project-response-rating-scale-select.helpers';
import { useRatingScaleSelect } from './project-response-rating-scale-select.hooks';
import RatingScaleStarSelect from './project-response-rating-scale-star-select';
import { ProjectResponseComponentProps } from './project-response-renderer.type';

interface RatingScaleProps {
  options: {
    label: string;
    value: string;
  }[];
  value: string;
  tentativeValue?: string;
  onClick: (value: string) => void;
  readonly?: boolean;
  hideLabels?: boolean;
  className?: string;
  optionRenderType?: ProjectResponseComponentProps['optionRenderType'];
  assets?: ProjectResponseComponentProps['assets'];
  onLabelChange?: (label: 'LOW' | 'HIGH', value: string) => void;
  labelMaxLength?: number;
  optionsWrapperClassName?: string;
  text?: string;
  contentClassName?: string;
  audio?: ProjectResponseAudio;
  isAiVoiceEnabled?: boolean;
  errorMessage?: string;
  labelsWrapperClassName?: string;
  writeLabelsDisabled?: boolean;
  app?: 'manager' | 'survey';
  numeralLocale?: string | null;
}

export default function RatingScaleSelect({
  app,
  options,
  value,
  tentativeValue,
  onClick,
  readonly,
  className,
  hideLabels,
  optionRenderType,
  assets,
  onLabelChange,
  labelMaxLength,
  optionsWrapperClassName,
  text,
  contentClassName,
  audio,
  isAiVoiceEnabled,
  errorMessage,
  labelsWrapperClassName,
  writeLabelsDisabled,
  numeralLocale,
}: RatingScaleProps) {
  const { sortedOptions, labels, hasLowLabelError, hasHighLabelError } =
    useRatingScaleSelect(options);
  const highlightedValue = getHighlightedRatingValue(value, tentativeValue);

  if (optionRenderType === OptionRenderType.Star) {
    return (
      <RatingScaleStarSelect
        options={sortedOptions}
        labels={labels}
        hasLowLabelError={hasLowLabelError}
        hasHighLabelError={hasHighLabelError}
        value={value}
        tentativeValue={tentativeValue}
        onClick={onClick}
        readonly={readonly}
        className={className}
        hideLabels={hideLabels}
        assets={assets}
        onLabelChange={onLabelChange}
        labelMaxLength={labelMaxLength}
        text={text}
        contentClassName={contentClassName}
        audio={audio}
        isAiVoiceEnabled={isAiVoiceEnabled}
        errorMessage={errorMessage}
        app={app}
      />
    );
  }

  const getRatingElement = (
    v: { value: string; label: string },
    index: number
  ) => {
    if (app) {
      return (
        <button
          key={`${v.value}-${index}`}
          data-selected={isRatingValueSelected(v.value, highlightedValue)}
          data-small={sortedOptions.length > 5}
          className='flex h-12 min-w-fit flex-1 items-center justify-center gap-2 rounded-full border border-(--brand-separation-border) bg-white p-2 text-center [font-family:var(--brand-font)] text-gray-900 shadow-(--brand-separation-shadow) active:ring-2 active:ring-sky-300 data-[option-render-type=Emoji]:text-2xl data-[option-render-type=Star]:text-xl data-[selected=true]:border-none data-[selected=true]:bg-(--brand-primary) data-[selected=true]:text-(--brand-primary-contrast) data-[selected=true]:shadow-xl data-[small=true]:h-9 data-[small=true]:min-w-0 data-[small=true]:p-1 data-[small=true]:text-sm'
          onClick={() => onClick(v.value)}
          disabled={readonly}
          data-test='rating-scale-button'
        >
          {renderValue(v, optionRenderType, sortedOptions, numeralLocale)}
        </button>
      );
    }

    return (
      <div
        data-selected={isRatingValueSelected(v.value, highlightedValue)}
        data-option-render-type={optionRenderType}
        className='flex h-10 min-h-10 min-w-0 flex-1 items-center justify-center rounded-[9px] bg-linear-to-r from-rose-100 to-indigo-100 hover:from-rose-200 hover:to-indigo-200 active:from-rose-400 active:to-indigo-400 data-[option-render-type=Emoji]:h-12 data-[option-render-type=Emoji]:min-h-12 data-[selected=true]:from-rose-400 data-[selected=true]:to-indigo-400'
        key={`${v.value}-${index}`}
        data-test='rating-scale-option'
      >
        <button
          data-selected={isRatingValueSelected(v.value, highlightedValue)}
          className='m-auto flex size-[calc(100%-2px)] flex-col items-center justify-center gap-2 rounded-lg bg-linear-to-r from-rose-50 to-indigo-50 p-2 hover:from-rose-100 hover:to-indigo-100 active:from-rose-100 active:to-indigo-100 active:ring-2 active:ring-sky-300 data-[selected=true]:from-rose-200 data-[selected=true]:to-indigo-200'
          onClick={() => onClick(v.value)}
          disabled={readonly}
          data-test='rating-scale-button'
        >
          <div
            data-option-render-type={optionRenderType}
            className='text-center font-sans text-gray-900 data-[option-render-type=Emoji]:text-2xl data-[option-render-type=Star]:text-xl'
          >
            {renderValue(v, optionRenderType, sortedOptions, numeralLocale)}
          </div>
        </button>
      </div>
    );
  };

  return (
    <div data-test='rating-scale-select' className='flex flex-col gap-2'>
      <div
        data-option-render-type={optionRenderType}
        data-warning={hasLowLabelError || hasHighLabelError}
        className={cn(
          'min-h-20 flex-col items-center justify-start gap-3 rounded-lg border p-3 placeholder:flex data-[warning=true]:border-orange-700',
          app
            ? 'border-(--brand-separation-border) bg-(--brand-assistant-message-bg) shadow-(--brand-separation-shadow) backdrop-blur-sm'
            : 'border-gray-200 bg-white',
          className
        )}
      >
        {isAiVoiceEnabled && (
          <ProjectResponseMessageRendererAudio audio={audio ?? undefined} />
        )}

        {text && (
          <ProjectResponseQuestionText
            text={text}
            className='text-(--brand-text-on-assistant-message)'
          />
        )}

        {assets && assets?.length > 0 && assets[0]?.url && (
          <ProjectResponseAssetItem asset={assets[0]} />
        )}

        <div
          data-hide-labels={hideLabels}
          className={cn(
            'flex flex-col items-start justify-start gap-2 self-stretch data-[hide-labels=false]:min-h-14',
            contentClassName
          )}
        >
          <div
            data-option-render-type={optionRenderType}
            data-small={sortedOptions.length > 5}
            className={cn(
              'flex items-center justify-center gap-2 sm:data-[option-render-type=Emoji]:flex-nowrap',
              app ? 'w-full data-[small=true]:gap-0.5' : 'self-stretch',
              optionsWrapperClassName
            )}
          >
            {sortedOptions.map((v, index) => getRatingElement(v, index))}
          </div>

          {!hideLabels && (
            <div
              className={cn(
                'flex flex-col gap-2 self-stretch',
                labelsWrapperClassName
              )}
            >
              <div className='flex items-start justify-between'>
                {labels.map((label, index) => (
                  <div
                    key={index}
                    data-position={index === 0 ? 'low' : 'high'}
                    className='group flex items-center gap-1 text-center [font-family:var(--brand-font)] text-xs text-(--brand-text-on-assistant-message) data-[position=high]:flex-row-reverse'
                    data-test={
                      index === 0
                        ? 'rating-scale-label-low'
                        : 'rating-scale-label-high'
                    }
                  >
                    {!onLabelChange ? (
                      label
                    ) : (
                      <input
                        placeholder={index === 0 ? 'very bad' : 'very good'}
                        disabled={writeLabelsDisabled}
                        maxLength={labelMaxLength}
                        className='w-min min-w-min appearance-none ring-0 group-data-[position=high]:text-right group-data-[position=low]:text-left'
                        value={label}
                        onChange={(e) =>
                          onLabelChange(
                            index === 0 ? 'LOW' : 'HIGH',
                            e.target.value
                          )
                        }
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error message outside the container */}
      {(hasLowLabelError || hasHighLabelError) && (
        <div className='ml-4 flex items-center gap-2 text-xs'>
          <FontAwesomeIcon
            icon={faWarning}
            className='size-4 text-orange-500'
          />
          <span className='text-orange-700'>
            {errorMessage ??
              "Endpoint labels need to be updated. Use 'Improve question' to generate them automatically."}
          </span>
        </div>
      )}
    </div>
  );
}
